import * as React from 'react';
import { DragDropProvider } from '@dnd-kit/react';
import { NavItem, NavItemRef } from 'Interfaces/Admin';
import { useAppDispatch, useAppSelector } from 'index';
import { navItemsSelector } from 'reducers/admin/selectors';
import { showColumns, showDetail } from 'reducers/admin/ui';
import Column from './Column';
import { useReparent } from './useReparent';

interface Props {
  /** Rendered as the rightmost column, e.g. the form for the selected item. */
  trailing?: React.ReactNode;
}

/**
 *  The horizontally scrolling stack of group/user columns, replacing
 *  SplitterView.js and the jqxDockPanel it was built on.
 *
 *  The stack is derived from the selection path: column N lists the children of
 *  the Nth item on the path, so the item selected in column N is the one whose
 *  children fill column N+1.
 */
export default function HierarchyNav(props: Props) {
  const dispatch = useAppDispatch();
  const items = useAppSelector(navItemsSelector);
  const path = useAppSelector((state) => state.admin.ui.path);
  const reparent = useReparent();

  const scroller = React.useRef<HTMLDivElement>(null);

  const byRef = React.useCallback(
    (ref: NavItemRef): NavItem | undefined =>
      items.find((i) => i.type === ref.type && i.id === ref.id),
    [items],
  );

  const columns = React.useMemo(
    () => path.map((ref) => byRef(ref)?.children || []),
    [path, byRef],
  );

  // Keep the newest column in view, the way the legacy scrollLeft nudge did.
  React.useEffect(() => {
    if (scroller.current) {
      scroller.current.scrollLeft = scroller.current.scrollWidth;
    }
  }, [path.length, props.trailing]);

  const openItem = (item: NavItem) => {
    dispatch(showDetail({ kind: item.type, id: item.id }));
  };

  const drillInto = (item: NavItem, columnIndex: number) => {
    dispatch(showColumns({
      path: [...path.slice(0, columnIndex + 1), { type: item.type, id: item.id }],
    }));
  };

  return (
    <DragDropProvider
      onDragEnd={({ operation }) => {
        const { source, target } = operation;
        if (!source || !target) {
          return;
        }
        const targetGroupId = Number(target.data?.id);
        const sourceRef = source.data as unknown as NavItemRef;
        if (sourceRef && Number.isFinite(targetGroupId)) {
          reparent({ type: sourceRef.type, id: Number(sourceRef.id) }, targetGroupId);
        }
      }}
    >
      <div className="admin-columns" ref={scroller}>
        {columns.map((columnItems, index) => (
          <Column
            key={`admin-column-${index}`}
            items={columnItems}
            selected={path[index + 1] || null}
            isTrail={index + 2 < path.length}
            onSelect={openItem}
            onDrillDown={(item) => drillInto(item, index)}
          />
        ))}
        {props.trailing}
      </div>
    </DragDropProvider>
  );
}
